import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { WMButtonVariant, WMFormControlLabel, WMRadio, WMSelect, WMTextField } from '@walkme/wm-ui';
import { Button, RadioGroup } from '@material-ui/core';
import { create } from 'react-modal-promise';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import useSelector from '../../../../../infrastructure/useSelector';

import { INotificationRecurrence } from '@communication-center/data-objects';
import DateTimePicker from '../DateTimePicker/DateTimePicker';
import * as actions from '../../../../../state/actions/editNotification';
import StyledWMDialog from '../../../../Dialogs/StyledWMDialog/StyledWMDialog';
import { days, getNumberOfDayInMonth, numbersStr } from './SelectReoccurrence';

import classes from './SelectReoccurrence.module.scss';
import { EndReoccurrenceSelect } from './EndReocurrence';
import dayjs from 'dayjs';

const keyPrefix = 'components.editNotification.editNotificationContent.sendStepDrawer.selectReoccurrence.customReoccurrenceModal';
const selectReocurrencePrefix = 'components.editNotification.editNotificationContent.sendStepDrawer.selectReoccurrence.selectReoccurrence';

export enum RepeatTypeSelect {
    DAILY,
    WEEKLY,
    MONTHLY,
    ANNUALLY
}

export const getUnitFromRepeatType = (repeatType: $TSFixMe) => {
    let unit: 'd' | 'w' | 'M' | 'y' = 'd';

    if (repeatType == RepeatTypeSelect.WEEKLY) unit = 'w';
    else if (repeatType == RepeatTypeSelect.MONTHLY) unit = 'M';
    else if (repeatType == RepeatTypeSelect.ANNUALLY) unit = 'y';

    return unit;
};

export enum MonthTypeSelect {
    MONTHLY_ON_WEEK_ON_DAY,
    MONTHLY_ON_MONTH_DAY
}

enum EndOptions {
    DATE,
    TIMES
}

const defaultCustomReoccurrenceData: INotificationRecurrence = {
    activePeriod: {
        type: 'days',
        amount: 1
    },
    dayRules: {
        daysRepeat: 1
    }
};

const topReoccurrenceRule = (recurrenceData: INotificationRecurrence) =>
    recurrenceData?.yearRules || recurrenceData?.monthRules || recurrenceData?.weekRules || recurrenceData?.dayRules;

const CustomReoccurrenceSettings = ({
    startDate,
    endDate,
    currentReoccurrenceData = null,
    onResolve,
    onChange
}: {
    startDate: Date;
    endDate: Date;
    currentReoccurrenceData?: INotificationRecurrence | null;
    onResolve: (x?: $TSFixMe) => $TSFixMe | void;
    onReject: (reason?: $TSFixMe) => $TSFixMe | void;
    onChange: (newReoccurrenceData: INotificationRecurrence, startDate: Date, endDate: Date) => $TSFixMe | void;
}) => {
    const { t } = useTranslation('', { keyPrefix });
    const {t: tt } = useTranslation('', { keyPrefix: selectReocurrencePrefix });
    const locale = useSelector(state => state.ui.languageCode);
    const dispatch = useDispatch();
    const useFormMethods = useForm();
    const {handleSubmit} = useFormMethods;

    let normalizedData = currentReoccurrenceData;
    if (!normalizedData || !topReoccurrenceRule(normalizedData)) normalizedData = defaultCustomReoccurrenceData;

    const [customRecurrenceData, setCustomRecurrenceData] = useState(normalizedData);

    const [newEndDate, setNewEndDate] = useState(endDate);

    const [newStartDate, setNewStartDate] = useState(startDate);

    const [processing, setProcessing] = useState(false);

    const [repeatType, setRepeatType] = useState(
        customRecurrenceData?.yearRules
            ? RepeatTypeSelect.ANNUALLY
            : customRecurrenceData?.monthRules
            ? RepeatTypeSelect.MONTHLY
            : customRecurrenceData?.weekRules
            ? RepeatTypeSelect.WEEKLY
            : customRecurrenceData?.dayRules
            ? RepeatTypeSelect.DAILY
            : undefined
    );

    const [repeatEvery, setRepeatEvery] = useState(
        customRecurrenceData?.yearRules?.yearsRepeat ||
            customRecurrenceData?.monthRules?.monthsRepeat ||
            customRecurrenceData?.weekRules?.weeksRepeat ||
            customRecurrenceData?.dayRules?.daysRepeat ||
            undefined
    );

    const [endsOption, setEndsOption] = useState(endDate ? EndOptions.DATE : null);

    const [endDateAfter, setEndDateAfter] = useState(0);

    const repeatOptions = useMemo(() => {
        const options = [];
        if (dayjs(newEndDate).diff(dayjs(startDate), 'hours', true) >= 24 || endsOption == EndOptions.TIMES)
            options.push({
          label: t('days'),
          value: RepeatTypeSelect.DAILY
            });

        if (dayjs(newEndDate).diff(dayjs(startDate), 'weeks') >= 1 || endsOption == EndOptions.TIMES)
            options.push({
          label: t('weeks'),
          value: RepeatTypeSelect.WEEKLY
            });

        if (dayjs(newEndDate).diff(dayjs(startDate), 'months') >= 1 || endsOption == EndOptions.TIMES)
            options.push({
          label: t('months'),
          value: RepeatTypeSelect.MONTHLY
            });

        if (dayjs(newEndDate).diff(dayjs(startDate), 'years') >= 1 || endsOption == EndOptions.TIMES)
            options.push({
          label: t('years'),
          value: RepeatTypeSelect.ANNUALLY
            });

        return options;
    }, [newEndDate, startDate, endsOption, t]);

    const repeatEveryMaxDifference = useCallback(() => {
        return dayjs(newEndDate).startOf('day').diff(dayjs(newStartDate).startOf('day'), getUnitFromRepeatType(repeatType));
    }, [newEndDate, newStartDate, repeatType]);

    const updateRepeatEvery = useCallback(
        (event: ChangeEvent<HTMLInputElement>, isBlur = false) => {
            const value = parseInt(event.target.value);

            if (!value || value < 1) {
                setRepeatEvery(isBlur ? 1 : value);
                return;
            }

            if (endsOption == EndOptions.TIMES || dayjs(newStartDate).add(value, getUnitFromRepeatType(repeatType)).toDate() <= newEndDate) {
              setRepeatEvery(value);
            } else {
                setRepeatEvery(repeatEveryMaxDifference());
            }
        },

        [newStartDate, repeatType, newEndDate, endsOption, repeatEveryMaxDifference]
    );

    const toggleWeekDay = useCallback(
        (i: number) => {
            const newDaysInWeek = customRecurrenceData?.dayRules?.daysInWeek?.concat([]) || [];

            const currentDayIndex = newDaysInWeek.indexOf(i);
            if (currentDayIndex > -1) {
                if (newDaysInWeek.length > 1) newDaysInWeek.splice(currentDayIndex, 1);
            } else {
                newDaysInWeek.push(i);
            }

            //update newStartDate
            const currentDay = startDate.getDay();
            const inSameWeekDays = newDaysInWeek.filter(weekDay => weekDay >= currentDay);
            const inNextWeekDays = newDaysInWeek.filter(weekDay => weekDay < currentDay);
            const earlierDays = inSameWeekDays.length > 0 ? inSameWeekDays : inNextWeekDays;
            const earlierDay = Math.min(...earlierDays);
            let daysGap = earlierDay - currentDay;
            if (daysGap < 0) daysGap += 7;
            const updatedStartDate = dayjs(startDate).add(daysGap, 'day').toDate();

            setNewStartDate(updatedStartDate);

            const updatedCustomRecurrenceData = {
                ...customRecurrenceData,
                dayRules: {
                    ...customRecurrenceData?.dayRules,
                    daysInWeek: newDaysInWeek
                }
            };

            setCustomRecurrenceData(updatedCustomRecurrenceData);
        },

        [customRecurrenceData, startDate]
    );

    useEffect(() => {
        if (repeatType != RepeatTypeSelect.WEEKLY) setNewStartDate(startDate);
    }, [repeatType, startDate]);

    const [monthRepeatType, setMonthRepeatType] = useState(
        customRecurrenceData?.monthRules?.monthsRepeat && customRecurrenceData?.dayRules?.daysInMonth
            ? MonthTypeSelect.MONTHLY_ON_MONTH_DAY
            : customRecurrenceData?.monthRules?.monthsRepeat &&
              customRecurrenceData?.weekRules?.weeksInMonth &&
              customRecurrenceData?.dayRules?.daysInWeek
            ? MonthTypeSelect.MONTHLY_ON_WEEK_ON_DAY
            : MonthTypeSelect.MONTHLY_ON_MONTH_DAY
    );

    const monthOptions = useMemo(() => {
        const options: $TSFixMe[] = [];

        if (!startDate) return options;

        options.push({
            label: t('monthOptions.monthly', {
          number: tt(`numbersStr.${numbersStr[getNumberOfDayInMonth(startDate)]}`),
          startDate: dayjs(startDate).locale(locale).format('dddd')
            }),
            value: MonthTypeSelect.MONTHLY_ON_WEEK_ON_DAY
        });

        options.push({
            label: t('monthOptions.monthlyOnDay', {
                startDate: startDate.getDate()
            }),
            value: MonthTypeSelect.MONTHLY_ON_MONTH_DAY
        });

        return options;
    }, [locale, startDate, t, tt]);

    useEffect(() => {
        setCustomRecurrenceData(customRecurrenceData => {
            const recurrenceData: INotificationRecurrence = {
                activePeriod: {
                    type: 'days',
                    amount: 1
                }
            };

            switch (repeatType) {
                case RepeatTypeSelect.DAILY:
                    recurrenceData.dayRules = {
                        daysRepeat: repeatEvery
                    };
                    break;
                case RepeatTypeSelect.WEEKLY:
                    recurrenceData.weekRules = {
                        weeksRepeat: repeatEvery
                    };
                    recurrenceData.dayRules = {
                        daysInWeek: customRecurrenceData.dayRules?.daysInWeek || [newStartDate.getDay()]
                    };
                    break;
                case RepeatTypeSelect.MONTHLY:
                    recurrenceData.monthRules = {
                        monthsRepeat: repeatEvery
                    };
                    if (monthRepeatType == MonthTypeSelect.MONTHLY_ON_WEEK_ON_DAY) {
                        recurrenceData.weekRules = {
                            weeksInMonth: customRecurrenceData.weekRules?.weeksInMonth || [getNumberOfDayInMonth(newStartDate)]
                        };
                        recurrenceData.dayRules = {
                            daysInWeek: customRecurrenceData.dayRules?.daysInWeek || [newStartDate.getDay()]
                        };
                    } else {
                        recurrenceData.dayRules = {
                            daysInMonth: customRecurrenceData.dayRules?.daysInMonth || [newStartDate.getDate()]
                        };
                    }
                    break;
                case RepeatTypeSelect.ANNUALLY:
                    recurrenceData.yearRules = {
                        yearsRepeat: repeatEvery
                    };
                    recurrenceData.monthRules = {
                        monthsInYear: customRecurrenceData.monthRules?.monthsInYear || [newStartDate.getMonth()]
                    };
                    recurrenceData.dayRules = {
                        daysInMonth: customRecurrenceData.dayRules?.daysInMonth || [newStartDate.getDate()]
                    };
                    break;
            }
            return { ...customRecurrenceData, ...recurrenceData };
        });
    }, [repeatType, repeatEvery, monthRepeatType, newStartDate]);

    useEffect(() => {
        const unit = getUnitFromRepeatType(repeatType);

        if (endsOption == EndOptions.TIMES) {
            let repeatStartDate = newStartDate;
            if (repeatType == RepeatTypeSelect.WEEKLY) {
                // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                if (customRecurrenceData?.dayRules?.daysInWeek?.length > 0) {
                    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                    const daysInWeek = customRecurrenceData.dayRules.daysInWeek;
                    const currentDay = newStartDate.getDay();
                    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                    const inSameWeekDays = daysInWeek.filter(weekDay => weekDay >= currentDay);
                    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                    const inNextWeekDays = daysInWeek.filter(weekDay => weekDay < currentDay);
                    const latestDays = inNextWeekDays.length > 0 ? inNextWeekDays : inSameWeekDays;
                    const latestDay = Math.max(...latestDays);
                    let daysGap = latestDay - currentDay;
                    if (daysGap < 0) daysGap += 7;
                    repeatStartDate = dayjs(newStartDate).add(daysGap, 'day').toDate();
                }
            }

            // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
            const newEndDate = dayjs(repeatStartDate).add(endDateAfter * repeatEvery, unit);

            if (dayjs(newEndDate).isValid()) setNewEndDate(newEndDate.toDate());
        }
    }, [endsOption, endDateAfter, repeatType, repeatEvery, customRecurrenceData, newStartDate]);

    const [MaxOcurrences, setMaxOcurrences] = useState(undefined);

    const updateEndAfter = useCallback(
        (value: number) => {
            if (value && value > 0) {
                const newValue = Math.floor(value);

                // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                if (dayjs(dayjs(newStartDate).add(newValue * repeatEvery, getUnitFromRepeatType(repeatType))).isValid()) {
                  setEndDateAfter(newValue);
                } else {
                    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
                    setMaxOcurrences(value - 1);
                }
            }
        },

        [newStartDate, repeatEvery, repeatType]
    );

    useEffect(() => {
        const unit = getUnitFromRepeatType(repeatType);

        if (endsOption == EndOptions.DATE && dayjs(newStartDate).isValid() && dayjs(newEndDate).isValid()) {
            // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
            setEndDateAfter(Math.floor(dayjs(newEndDate).diff(dayjs(newStartDate), unit) / repeatEvery));
        }
    }, [endsOption, newEndDate, repeatType, repeatEvery, newStartDate]);

    const endAtDisabledDates = useCallback(
        (day: Date) => {
            return newStartDate
              ? dayjs(newStartDate).add(repeatEvery ?? 0, getUnitFromRepeatType(repeatType)).diff(day, 'day', false) > 0
              : day <= new Date();
        },
        [newStartDate, repeatType, repeatEvery]
    );

    const onDecline = useCallback(async () => {
        onResolve();
    }, [onResolve]);

    const onConfirm = useCallback(async () => {
        if (processing) {
            return;
        }

        setProcessing(true);

        if (newEndDate.valueOf() != endDate.valueOf()) {
            await dispatch(
                actions.publishStatePropertyChanged({
                    endAt: newEndDate
                })
            );
        }

        if (newStartDate.valueOf() != startDate.valueOf()) {
            await dispatch(
                actions.publishStatePropertyChanged({
                    startAt: newStartDate
                })
            );
        }

        onChange(customRecurrenceData, newStartDate, newEndDate);
        onResolve();
    }, [processing, newEndDate, endDate, newStartDate, startDate, onChange, customRecurrenceData, onResolve, dispatch]);

    const buttons = [
        {
            children: t('cancel'),
            variant: WMButtonVariant.Primary,
            className: classes.cancelButton,
            onClick: () => onDecline(),
            'data-testid': 'custom-reoccurrence-dialog-decline'
        },
        {
            children: t('done'),
            variant: WMButtonVariant.Primary,
            onClick: handleSubmit(onConfirm),
            loading: processing,
            'data-testid': 'custom-reoccurrence-dialog-confirm'
        }
    ];

    const validateInput = (event: $TSFixMe) => {
        const regex = new RegExp('^[0-9]*$');
        if (!regex.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab' && !((event.ctrlKey || event.metaKey) && event.key === 'a')) {
            event.preventDefault();
        }
    };

    const onEndRecurrenceButtonsSelect = useCallback((newEndButtons: any) => {
        setCustomRecurrenceData(state => ({ ...state, endRecurrenceButtons: newEndButtons }));
    }, []);

    return (
      <FormProvider {...useFormMethods}>
        <StyledWMDialog
            className={classes.modal}
            title={t('customRecurrence')}
            open
            data-testid="custom-reoccurrence-dialog"
            onClose={onDecline}
            buttons={buttons}
            classes={{ content: classes.dialogContent }}
        >
            <div data-testid="custom-reoccurrence-settings" style={{ width: '100%', minHeight: 240 }}>
                <div data-testid="custom-reoccurrence-repeat-every-row" className={classes.row}>
                    <div className={classes.settingTitle}>{t('repeatEvery')}</div>
                    <div className={classes.alignToRight}>
                        <WMTextField
                            className={classes.numberInput}
                            type="number"
                            onKeyDown={validateInput}
                            onChange={updateRepeatEvery}
                            onBlur={event => updateRepeatEvery(event as ChangeEvent<HTMLInputElement>, true)}
                            inputProps={{
                                'data-testid': 'repeat-every-input',
                                min: '1',
                                max: repeatEveryMaxDifference(),
                                // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                                value: repeatEvery.toString()
                            }}
                        />
                        <WMSelect
                            className={classes.selectDays}
                            options={repeatOptions}
                            value={repeatOptions.find(option => option.value === repeatType)}
                            onChange={(type: $TSFixMe) => {
                                setRepeatType(type.value);
                            }}
                        />
                    </div>
                </div>

                {repeatType == RepeatTypeSelect.WEEKLY && (
                    <div data-testid="custom-reoccurrence-repeat-on-week-row" className={classes.row}>
                        <div className={classes.settingTitle}>{t('repeatOn')}</div>
                        <div className={`${classes.alignToRight}`}>
                            {[...Array(7)].map((x, i) => (
                                <Button
                                    variant="outlined"
                                    key={i}
                                    classes={{
                                        outlined: customRecurrenceData?.dayRules?.daysInWeek?.includes(i)
                                            ? classes.selectedDay
                                            : classes.unSelectedDay
                                    }}
                                    className={classes.dayBtn}
                                    onClick={() => {
                                        toggleWeekDay(i);
                                    }}
                                >
                                    {tt(`days.${days[i]}`)[0]}
                                </Button>
                            ))}
                        </div>
                    </div>
                )}
                {repeatType == RepeatTypeSelect.MONTHLY && (
                    <WMSelect
                        className={classes.marginTop}
                        options={monthOptions}
                        value={monthOptions.find(option => option.value === monthRepeatType)}
                        onChange={(type: $TSFixMe) => {
                            setMonthRepeatType(type.value);
                        }}
                    />
                )}
                <div className={classes.ends} data-testid="custom-reoccurrence-repeat-ends-row">
                    <div className={classes.settingTitle}>{t('ends')}</div>
                    <div data-testid="custom-reoccurrence-repeat-ends-options">
                        <RadioGroup
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                setEndsOption(Number(event.target.value));
                            }}
                        >
                            <WMFormControlLabel
                                control={<WMRadio defaultIcon checked={endsOption == EndOptions.DATE} />}
                                value={EndOptions.DATE}
                                label={
                                    <div className={classes.row}>
                                        <div className={classes.endsLabel}>{t('on')}</div>
                                        <DateTimePicker
                                            disabled={endsOption != EndOptions.DATE}
                                            value={newEndDate}
                                            onDateTimeChange={(dateTime: Date) => {
                                                const selectedDateTime = new Date(dateTime);
                                                setNewEndDate(selectedDateTime);
                                            }}
                                            disabledDays={endAtDisabledDates}
                                            data-testid="custom-reoccurrence-ends-on"
                                        />
                                    </div>
                                }
                            />
                            <WMFormControlLabel
                                control={<WMRadio defaultIcon checked={endsOption == EndOptions.TIMES} />}
                                value={EndOptions.TIMES}
                                label={
                                    <div className={classes.row}>
                                        <div className={classes.endsLabel}>{t('after')}</div>
                                        <div className={classes.endsAfter} data-testid="custom-reoccurrence-repeat-ends-after">
                                            <WMTextField
                                                className={classes.numberInput}
                                                type="number"
                                                onKeyDown={validateInput}
                                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                    if (Number(event.target.value) > 0) {
                                                        updateEndAfter(parseInt(event.target.value));
                                                    }
                                                }}
                                                disabled={endsOption != EndOptions.TIMES}
                                                inputProps={{
                                                    'data-testid': 'ends-after-input',
                                                    min: '1',
                                                    max: MaxOcurrences,
                                                    value: endDateAfter.toString()
                                                }}
                                            />
                                            <span>{t('occurrences')}</span>
                                        </div>
                                    </div>
                                }
                            />
                        </RadioGroup>

                        <div className={classes.modalEndRecurranceInput} data-testid={'custom-reoccurrence-end-recurrence-option-select-title'}>
                          <span>{tt('endRecurrenceAction')}</span>
                        </div>
                        <div className={classes.modalEndRecurranceInput}>
                            <EndReoccurrenceSelect
                                reoccurrenceEndButtons={customRecurrenceData?.endRecurrenceButtons}
                                onChange={onEndRecurrenceButtonsSelect}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </StyledWMDialog>
        </FormProvider>
    );
};

export const customReoccurrenceModal = create(CustomReoccurrenceSettings as $TSFixMe);
